import { Component, computed, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { type VirtualRoom, VirtualRoomsService } from '@api/uc-monitoring';
import { map, type Observable, of, switchMap } from 'rxjs';

const PAGE_SIZE = 100;

@Component({
  imports: [MatAutocompleteModule, MatFormFieldModule, MatInputModule],
  selector: 'avs-room-email-picker',
  templateUrl: './room-email-picker.component.html',
})
export class RoomEmailPickerComponent implements OnInit {
  spaceId = input<string | undefined>(undefined);

  inputClass = input('input-main');

  showLabel = input(true);

  requireSave = input(false);

  valueChange = output<string>();

  protected typedValue = signal('');
  protected editing = signal(false);
  protected isEditing = computed(() => this.requireSave() && this.editing());
  protected isReadonly = computed(() => this.requireSave() && !this.editing());
  protected canClear = computed(() => !!this.typedValue() && !this.isReadonly());
  protected linkableRooms = computed(() =>
    this.allRooms().filter(room => !!room.email && (!room.spaceId || room.spaceId === this.spaceId())),
  );
  protected filteredRooms = computed(() => {
    const typed = this.typedValue().toLowerCase();

    return this.linkableRooms().filter(room => room.email!.toLowerCase().includes(typed));
  });

  private allRooms = signal<VirtualRoom[]>([]);
  private pendingVirtualRoomId = '';
  private virtualRoomsService = inject(VirtualRoomsService);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.loadAllRooms().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(rooms => {
      this.allRooms.set(rooms);

      const currentlyLinked = rooms.find(room => room.spaceId === this.spaceId());

      this.typedValue.set(currentlyLinked?.email ?? '');
      this.pendingVirtualRoomId = currentlyLinked?.id ?? '';

      this.valueChange.emit(this.pendingVirtualRoomId);
    });
  }

  reset() {
    this.typedValue.set('');
    this.pendingVirtualRoomId = '';
  }

  protected onInput(value: string) {
    this.typedValue.set(value);

    const typed = value.trim().toLowerCase();
    const virtualRoomId = this.linkableRooms().find(room => room.email!.toLowerCase() === typed)?.id ?? '';

    if (virtualRoomId === this.pendingVirtualRoomId) {
      return;
    }

    this.pendingVirtualRoomId = virtualRoomId;

    if (!this.requireSave()) {
      this.valueChange.emit(virtualRoomId);
    }
  }

  protected onOptionSelected(virtualRoomId: string) {
    const room = this.allRooms().find(candidate => candidate.id === virtualRoomId);

    this.typedValue.set(room?.email ?? '');
    this.pendingVirtualRoomId = virtualRoomId;

    if (!this.requireSave()) {
      this.valueChange.emit(virtualRoomId);
    }
  }

  protected onClear() {
    this.typedValue.set('');
    this.pendingVirtualRoomId = '';

    if (!this.requireSave()) {
      this.valueChange.emit('');
    }
  }

  protected onEdit() {
    this.editing.set(true);
  }

  protected onSave() {
    this.valueChange.emit(this.pendingVirtualRoomId);
    this.editing.set(false);
  }

  private loadAllRooms(offset = 0): Observable<VirtualRoom[]> {
    return this.virtualRoomsService.findAll({}, { limit: PAGE_SIZE, offset }).pipe(
      switchMap(({ count = 0, data = [] }) => {
        const loaded = offset + data.length;

        return data.length === 0 || loaded >= count
          ? of(data)
          : this.loadAllRooms(loaded).pipe(map(rest => [...data, ...rest]));
      }),
    );
  }
}
